import clsx from 'clsx';
import { AlertTriangle, Check, FileSpreadsheet, Undo2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router';
import { addMonths, isWeekend, monthEnd, monthStart, weekStart } from '@pz/core';
import { BulkHoursDialog } from '../components/approvals-bulk';
import { BilledCell, BilledDialog, EntryDialog, TaskBadges } from '../components/entry';
import { ScrollBox, Sentinel, useChunked } from '../components/virtual';
import { Button, Checkbox, ConfirmModal, EmptyState, ErrorBox, Field, Input, Loading, PageHeader, Pill, Select, StatusPill, Tabs, useToast } from '../components/ui';
import { d, h, hh, kw, wd } from '../lib/format';
import { useM, useQ } from '../lib/hooks';
import { useCategories } from '../lib/categories';
import { useMe } from '../lib/me';
import { rowCheck, useRangeSelection } from '../lib/selection';
import type { EntryView } from '@pz/server';

/**
 * Freigaben: time entries of other people (project leads first stage, office or management final stage).
 * Absence requests are decided on their own page ("Abwesenheitsanträge"); an old link with ?tab=leave goes there.
 */
export function ApprovalsPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  if (params.get('tab') === 'leave') return <Navigate to="/leave-requests" replace />;
  // waiting entries, or the approved ones that can be returned to the person for a correction (round 5, request 9)
  const view = params.get('view') === 'approved' ? 'approved' : 'pending';
  const setView = (v: string) => {
    const next = new URLSearchParams(params);
    if (v === 'approved') next.set('view', 'approved');
    else next.delete('view');
    setParams(next, { replace: true });
  };
  return (
    <div className="pb-16">
      <Tabs
        value={view}
        onChange={setView}
        tabs={[
          { id: 'pending', label: t('approvals.views.pending') },
          { id: 'approved', label: t('approvals.views.approved') },
        ]}
      />
      {view === 'approved' ? <ApprovedEntries /> : <TimeApprovals />}
    </div>
  );
}

/** Status of the task of an entry; a closed task gets a light warning (why was it booked on?) (round 5, request 10). */
function TaskStatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  if (status === 'closed') {
    return (
      <Pill tone="warn" title={t('approvals.closedTaskHint')}>
        <AlertTriangle className="size-3" />
        {t('approvals.closedTask')}
      </Pill>
    );
  }
  return <Pill>{t(`task.status.${status as 'open'}`, { defaultValue: status })}</Pill>;
}

/** entries per page of the queue and per request of a bulk decision */
const PAGE = 2000;
const PAGE_MAX = 5000;
const BATCH = 1000;

function TimeApprovals() {
  const { t } = useTranslation();
  const { can } = useMe();
  const toast = useToast();
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [projectId, setProjectId] = useState('');
  const [reject, setReject] = useState(false);
  const [edit, setEdit] = useState<EntryView | null>(null);
  const [billed, setBilled] = useState<EntryView | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  // filters of the queue: person and kind of task (Forma, manual, category); long queues are read in pages
  const [personId, setPersonId] = useState('');
  const [task, setTask] = useState('');
  const [taskStatus, setTaskStatus] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const filterKey = `${scope}|${projectId}|${personId}|${task}|${taskStatus}`;
  const q = useQ('approvals.queue', { scope, projectId: projectId || undefined, personId: personId || undefined, task: (task || undefined) as 'forma' | undefined, taskStatus: taskStatus || undefined, limit });
  const cats = useCategories();
  const projects = useQ('projects.list', {});
  const inv = { invalidate: ['approvals.', 'time.', 'kpi.', 'billing.'] };
  const approve = useM('approvals.approve', inv);
  const rejectM = useM('approvals.reject', inv);
  const undo = useM('approvals.undo', inv);
  const items = q.data?.items ?? [];

  const groups = useMemo(() => {
    const map = new Map<string, { key: string; person: string; week: string; items: typeof items }>();
    for (const i of items) {
      const ws = weekStart(i.workDate);
      const key = `${i.personId}:${ws}`;
      const g = map.get(key) ?? { key, person: i.personName, week: ws, items: [] };
      g.items.push(i);
      map.set(key, g);
    }
    return [...map.values()].sort((a, b) => a.person.localeCompare(b.person) || a.week.localeCompare(b.week));
  }, [items]);
  const chunk = useChunked(groups, 20, filterKey);
  // visible order for Shift ranges across all groups
  const order = useMemo(() => groups.flatMap((g) => g.items.map((i) => i.id)), [groups]);
  const sel = useRangeSelection(order);
  const selected = sel.selected;

  const runUndo = (ids: string[]) =>
    undo
      .mutateAsync({ ids })
      .then((u) => {
        toast.ok(t('approvals.undone', { count: u.undone }));
        if (u.skipped.length) toast.warn(t('approvals.undoSkipped', { count: u.skipped.length }));
      })
      .catch(toast.error);

  const doApprove = async (ids: string[]) => {
    try {
      // large selections in parts of 1000 (one request each)
      let approved = 0;
      const skipped: { id: string }[] = [];
      for (let i = 0; i < ids.length; i += BATCH) {
        const r = await approve.mutateAsync({ ids: ids.slice(i, i + BATCH) });
        approved += r.approved;
        skipped.push(...r.skipped);
      }
      const done = ids.filter((id) => !skipped.some((x) => x.id === id));
      toast.undoable(t('approvals.done', { count: approved }), t('common.undo'), () => runUndo(done.slice(0, 2000)));
      if (skipped.length) toast.warn(t('approvals.skipped', { count: skipped.length }));
      sel.clear();
    } catch (e) {
      toast.error(e);
    }
  };

  return (
    <div>
      <PageHeader
        title={t('approvals.title')}
        subtitle={t('approvals.subtitle')}
        actions={
          <>
            <Button icon={<FileSpreadsheet className="size-4" />} onClick={() => setBulkOpen(true)} title={t('approvals.bulk.title')}>
              {t('approvals.bulk.open')}
            </Button>
            {can('time.manage') && (
              <Select className="w-44" value={scope} onChange={(e) => setScope(e.target.value as 'mine' | 'all')} aria-label={t('approvals.mine')}>
                <option value="mine">{t('approvals.mine')}</option>
                <option value="all">{t('approvals.all')}</option>
              </Select>
            )}
            <Select className="w-64" value={projectId} onChange={(e) => (setProjectId(e.target.value), setLimit(PAGE))} aria-label={t('common.project')}>
              <option value="">{t('common.all')}</option>
              {(projects.data?.projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.number} {p.name}
                </option>
              ))}
            </Select>
            <Select className="w-52" value={personId} onChange={(e) => (setPersonId(e.target.value), setLimit(PAGE))} aria-label={t('approvals.filterPerson')}>
              <option value="">{t('approvals.allPersons')}</option>
              {(q.data?.facets.persons ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.count})
                </option>
              ))}
              {personId && !(q.data?.facets.persons ?? []).some((p) => p.id === personId) && <option value={personId}>…</option>}
            </Select>
            <Select className="w-52" value={task} onChange={(e) => (setTask(e.target.value), setLimit(PAGE))} aria-label={t('approvals.filterKind')}>
              <option value="">{t('approvals.allKinds')}</option>
              {(q.data?.facets.kinds ?? []).map((k) => (
                <option key={k.key} value={k.key}>
                  {k.key === 'forma' ? t('approvals.kindForma') : k.key === 'manual' ? t('approvals.kindManual') : cats.label(k.key.slice(4))} ({k.count})
                </option>
              ))}
              {task && !(q.data?.facets.kinds ?? []).some((k) => k.key === task) && <option value={task}>…</option>}
            </Select>
            <Select className="w-48" value={taskStatus} onChange={(e) => (setTaskStatus(e.target.value), setLimit(PAGE))} aria-label={t('approvals.filterTaskStatus')}>
              <option value="">{t('approvals.allTaskStatuses')}</option>
              {(q.data?.facets.taskStatuses ?? []).map((k) => (
                <option key={k.key} value={k.key}>
                  {t(`task.status.${k.key as 'open'}`, { defaultValue: k.key })} ({k.count})
                </option>
              ))}
              {taskStatus && !(q.data?.facets.taskStatuses ?? []).some((k) => k.key === taskStatus) && <option value={taskStatus}>…</option>}
            </Select>
          </>
        }
      />
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && items.length === 0 && <EmptyState>{t('approvals.nothing')}</EmptyState>}
      {items.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
          <Checkbox label={t('common.selectAll')} checked={sel.all} onChange={sel.toggleAll} />
          <span className="text-xs text-graphite">{t('common.shiftHint')}</span>
          {q.data && (
            <span className="ml-auto text-xs text-graphite">
              {t('approvals.summaryLine', { shown: items.length, total: q.data.total, hours: h(q.data.totalMinutes) })}
            </span>
          )}
        </div>
      )}
      {q.data?.hasMore && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-line bg-sheet-2 px-3 py-2 text-sm">
          <span>{t('approvals.moreHint', { shown: items.length, total: q.data.total })}</span>
          {limit < PAGE_MAX && (
            <Button size="sm" loading={q.isFetching} onClick={() => setLimit(Math.min(PAGE_MAX, limit + PAGE))}>
              {t('approvals.loadMore')}
            </Button>
          )}
        </div>
      )}
      <ScrollBox maxHeight="min(74vh, 60rem)" label={t('approvals.title')} className="flex flex-col gap-4 pr-1">
        {chunk.visible.map((g) => {
          const ids = g.items.map((i) => i.id);
          const all = ids.every((id) => selected.has(id));
          const total = g.items.reduce((s, i) => s + i.minutes, 0);
          return (
            <section key={g.key} className="rounded-lg border border-line bg-sheet">
              <header className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-2">
                <Checkbox label={<span className="font-semibold">{g.person}</span>} checked={all} onChange={(e) => sel.setMany(ids, e.target.checked)} />
                <span className="num text-sm text-graphite">
                  {t('approvals.weekOf', { week: kw(g.week) })} · {d(g.week)}
                </span>
                <span className="num ml-auto text-sm font-semibold">{hh(total)}</span>
                <Button size="sm" icon={<Check className="size-3.5" />} onClick={() => doApprove(ids)} loading={approve.isPending}>
                  {t('approvals.approve')}
                </Button>
              </header>
              <ul>
                {g.items.map((i) => (
                  <li key={i.id} className={clsx('flex flex-wrap items-start gap-3 border-b border-line/60 px-4 py-2 text-sm last:border-0', selected.has(i.id) && 'bg-accent-soft/50')}>
                    <Checkbox label={<span className="sr-only">{t('common.select')}</span>} {...rowCheck(sel, i.id)} />
                    <span className={clsx('num w-24 shrink-0', isWeekend(i.workDate) ? 'font-medium text-warn' : 'text-graphite')} title={isWeekend(i.workDate) ? t('approvals.weekendHint') : undefined}>
                      {wd(i.workDate)} {d(i.workDate).slice(0, 6)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="num font-semibold text-accent">{i.taskRef}</span>
                        <span>{i.taskTitle}</span>
                        <TaskBadges formaState={i.taskFormaState} deleted={i.taskDeleted} />
                        <TaskStatusPill status={i.taskStatus} />
                      </div>
                      <div className="text-xs text-graphite">
                        {i.projectNumber} {i.projectName} · {t(`billingType.${i.billingType}`)}
                        {i.workLocation ? ` · ${i.workLocation}` : ''}
                      </div>
                      {i.description && <div className="mt-0.5 whitespace-pre-line text-graphite">{i.description}</div>}
                    </div>
                    <span className="flex flex-col items-end gap-1">
                      <span className="num font-semibold">{h(i.minutes)}</span>
                      <BilledCell entry={i} onEdit={setBilled} />
                      <span className="flex gap-1">
                        <StatusPill status={i.status} />
                        <Pill tone={i.stage === 'override' ? 'warn' : 'neutral'}>{t(`approvals.stage.${i.stage}`)}</Pill>
                      </span>
                      {i.perm.edit && (
                        <button className="text-xs text-accent hover:underline" onClick={() => setEdit(i)}>
                          {t('common.edit')}
                        </button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
        {chunk.hasMore && <Sentinel onMore={chunk.more} />}
      </ScrollBox>
      {chunk.hasMore && <p className="mt-2 text-xs text-graphite">{t('approvals.groupsShown', { shown: chunk.shown, total: chunk.total })}</p>}

      {selected.size > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-sheet px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2">
            <span className="text-sm">{t('common.selected', { count: selected.size })}</span>
            <span className="num text-sm text-graphite">
              {hh(items.filter((i) => selected.has(i.id)).reduce((s, i) => s + i.minutes, 0))}
            </span>
            <div className="ml-auto flex gap-2">
              <Button variant="danger" icon={<X className="size-4" />} onClick={() => setReject(true)}>
                {t('approvals.reject')}
              </Button>
              <Button variant="primary" icon={<Check className="size-4" />} loading={approve.isPending} onClick={() => doApprove([...selected])}>
                {t('approvals.approve')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={reject}
        onClose={() => setReject(false)}
        title={t('approvals.rejectTitle')}
        confirmLabel={t('approvals.reject')}
        danger
        reason="required"
        reasonLabel={t('approvals.rejectReason')}
        loading={rejectM.isPending}
        error={rejectM.error}
        onConfirm={async (reason) => {
          try {
            const ids = [...selected];
            let rejected = 0;
            const skipped: { id: string }[] = [];
            for (let i = 0; i < ids.length; i += BATCH) {
              const r = await rejectM.mutateAsync({ ids: ids.slice(i, i + BATCH), reason });
              rejected += r.rejected;
              skipped.push(...r.skipped);
            }
            const done = ids.filter((id) => !skipped.some((x) => x.id === id));
            toast.undoable(t('approvals.rejected', { count: rejected }), t('common.undo'), () => runUndo(done.slice(0, 2000)));
            sel.clear();
            setReject(false);
          } catch {
            // shown in the dialog
          }
        }}
      />
      <EntryDialog open={!!edit} entry={edit} onClose={() => setEdit(null)} />
      <BilledDialog entry={billed} onClose={() => setBilled(null)} />
      <BulkHoursDialog open={bulkOpen} onClose={() => setBulkOpen(false)} />
    </div>
  );
}

/**
 * Approved entries the actor may return to the person (round 5, request 9): an approved entry is changed by nobody; who
 * owns the approval returns it with a reason, the person corrects it and submits it again. Entries on a draft billing
 * document are marked (the document shows "data changed" afterwards); billed entries come back only when their document
 * is cancelled.
 */
function ApprovedEntries() {
  const { t } = useTranslation();
  const toast = useToast();
  const { me } = useMe();
  const [from, setFrom] = useState(monthStart(me.today));
  const [to, setTo] = useState(monthEnd(me.today));
  const [projectId, setProjectId] = useState('');
  const [personId, setPersonId] = useState('');
  const [taskStatus, setTaskStatus] = useState('');
  const [returnIds, setReturnIds] = useState<string[] | null>(null);
  const projects = useQ('projects.list', {});
  const q = useQ('approvals.approved', { from, to, projectId: projectId || undefined, personId: personId || undefined, taskStatus: taskStatus || undefined });
  const returnM = useM('approvals.return', { invalidate: ['approvals.', 'time.', 'kpi.', 'billing.'] });
  const items = q.data?.items ?? [];
  const order = useMemo(() => items.map((i) => i.id), [items]);
  const sel = useRangeSelection(order);
  const groups = useMemo(() => {
    const map = new Map<string, { key: string; person: string; week: string; items: typeof items }>();
    for (const i of items) {
      const ws = weekStart(i.workDate);
      const key = `${i.personId}:${ws}`;
      const g = map.get(key) ?? { key, person: i.personName, week: ws, items: [] };
      g.items.push(i);
      map.set(key, g);
    }
    return [...map.values()].sort((a, b) => a.person.localeCompare(b.person) || b.week.localeCompare(a.week));
  }, [items]);
  const chunk = useChunked(groups, 20, `${from}|${to}|${projectId}|${personId}|${taskStatus}`);
  const statuses = useMemo(() => [...new Set(items.map((i) => i.taskStatus))].sort(), [items]);
  const preset = (m: number) => {
    const s = addMonths(monthStart(me.today), m);
    setFrom(s);
    setTo(monthEnd(s));
  };
  const inDraft = (returnIds ?? []).filter((id) => items.find((i) => i.id === id)?.inDraftDocument).length;
  return (
    <div>
      <PageHeader title={t('approvals.approvedTitle')} subtitle={t('approvals.approvedSubtitle')} />
      <div className="mb-3 flex flex-wrap items-end gap-3 rounded-lg border border-line bg-sheet p-3">
        <div className="flex flex-wrap gap-1">
          <Button size="sm" onClick={() => preset(0)}>
            {t('entries.thisMonth')}
          </Button>
          <Button size="sm" onClick={() => preset(-1)}>
            {t('entries.lastMonth')}
          </Button>
        </div>
        <Field label={t('common.from')} htmlFor="approved-from">
          <Input id="approved-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
        </Field>
        <Field label={t('common.to')} htmlFor="approved-to">
          <Input id="approved-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="w-40" />
        </Field>
        <Select className="w-64" value={projectId} onChange={(e) => setProjectId(e.target.value)} aria-label={t('common.project')}>
          <option value="">{t('common.all')}</option>
          {(projects.data?.projects ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.number} {p.name}
            </option>
          ))}
        </Select>
        <Select className="w-52" value={personId} onChange={(e) => setPersonId(e.target.value)} aria-label={t('approvals.filterPerson')}>
          <option value="">{t('approvals.allPersons')}</option>
          {(q.data?.facets.persons ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        <Select className="w-48" value={taskStatus} onChange={(e) => setTaskStatus(e.target.value)} aria-label={t('approvals.filterTaskStatus')}>
          <option value="">{t('approvals.allTaskStatuses')}</option>
          {statuses.map((k) => (
            <option key={k} value={k}>
              {t(`task.status.${k as 'open'}`, { defaultValue: k })}
            </option>
          ))}
          {taskStatus && !statuses.includes(taskStatus) && <option value={taskStatus}>…</option>}
        </Select>
      </div>
      <p className="mb-3 text-xs text-graphite">{t('approvals.returnRule')}</p>
      {q.isLoading && <Loading />}
      <ErrorBox error={q.error} />
      {q.data && items.length === 0 && <EmptyState>{t('approvals.approvedEmpty')}</EmptyState>}
      {items.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
          <Checkbox label={t('common.selectAll')} checked={sel.all} onChange={sel.toggleAll} />
          <span className="text-xs text-graphite">{t('common.shiftHint')}</span>
          <span className="ml-auto text-xs text-graphite">{t('entries.count', { count: items.length })}</span>
        </div>
      )}
      <ScrollBox maxHeight="min(70vh, 56rem)" label={t('approvals.approvedTitle')} className="flex flex-col gap-4 pr-1">
        {chunk.visible.map((g) => (
          <section key={g.key} className="rounded-lg border border-line bg-sheet">
            <header className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-2">
              <Checkbox label={<span className="font-semibold">{g.person}</span>} checked={g.items.every((i) => sel.selected.has(i.id))} onChange={(e) => sel.setMany(g.items.map((i) => i.id), e.target.checked)} />
              <span className="num text-sm text-graphite">
                {t('approvals.weekOf', { week: kw(g.week) })} · {d(g.week)}
              </span>
              <span className="num ml-auto text-sm font-semibold">{hh(g.items.reduce((s, i) => s + i.minutes, 0))}</span>
            </header>
            <ul>
              {g.items.map((i) => (
                <li key={i.id} className={clsx('flex flex-wrap items-start gap-3 border-b border-line/60 px-4 py-2 text-sm last:border-0', sel.selected.has(i.id) && 'bg-accent-soft/50')}>
                  <Checkbox label={<span className="sr-only">{t('common.select')}</span>} {...rowCheck(sel, i.id)} />
                  <span className="num w-24 shrink-0 text-graphite">
                    {wd(i.workDate)} {d(i.workDate).slice(0, 6)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="num font-semibold text-accent">{i.taskRef}</span>
                      <span>{i.taskTitle}</span>
                      <TaskBadges formaState={i.taskFormaState} deleted={i.taskDeleted} />
                      <TaskStatusPill status={i.taskStatus} />
                      {i.inDraftDocument && (
                        <Pill tone="warn" title={t('approvals.inDraftHint')}>
                          {t('approvals.inDraft')}
                        </Pill>
                      )}
                    </div>
                    <div className="text-xs text-graphite">
                      {i.projectNumber} {i.projectName} · {t(`billingType.${i.billingType}`)}
                    </div>
                    {i.description && <div className="mt-0.5 whitespace-pre-line text-graphite">{i.description}</div>}
                  </div>
                  <span className="flex flex-col items-end gap-1">
                    <span className="num font-semibold">{h(i.minutes)}</span>
                    <StatusPill status={i.status} />
                    <Button size="sm" icon={<Undo2 className="size-3.5" />} onClick={() => setReturnIds([i.id])}>
                      {t('approvals.return')}
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {chunk.hasMore && <Sentinel onMore={chunk.more} />}
      </ScrollBox>
      {sel.count > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-sheet px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-2">
            <span className="text-sm">{t('common.selected', { count: sel.count })}</span>
            <div className="ml-auto flex gap-2">
              <Button variant="danger" icon={<Undo2 className="size-4" />} onClick={() => setReturnIds([...sel.selected])}>
                {t('approvals.return')}
              </Button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        open={!!returnIds}
        onClose={() => setReturnIds(null)}
        title={t('approvals.returnTitle', { count: returnIds?.length ?? 0 })}
        text={`${t('approvals.returnText')}${inDraft ? ` ${t('approvals.returnDraft', { count: inDraft })}` : ''}`}
        confirmLabel={t('approvals.return')}
        danger
        reason="required"
        reasonLabel={t('approvals.returnReason')}
        loading={returnM.isPending}
        error={returnM.error}
        onConfirm={async (reason) => {
          try {
            const r = await returnM.mutateAsync({ ids: returnIds ?? [], reason });
            toast.ok(t('approvals.returned', { count: r.returned }));
            if (r.skipped.length) toast.warn(t('approvals.returnSkipped', { count: r.skipped.length }));
            sel.clear();
            setReturnIds(null);
          } catch {
            // shown in the dialog
          }
        }}
      />
    </div>
  );
}
